// Service API pour récupérer les données Hanafiya (exposants, événements, daaras, marabouts)
import api from '../config/api';

export interface Expose {
  id: number;
  nom_exposant: string;
  prenom_exposant: string;
  theme: string;
  description?: string;
  date_expose: string;
  heure_debut: string;
  lieu: string;
  type_expose: string;
  section: string;
}

export interface Event {
  id: number;
  titre: string;
  description: string;
  type_event: string;
  date_event: string;
  heure_debut: string;
  lieu: string;
  section: string;
}

export interface Daara {
  id: number;
  titre: string;
  description: string;
  jour: string;
  section: string;
}

export interface Marabout {
  id: number;
  nom_complet: string;
  nom_arabe?: string;
  biographie: string;
  date_naissance?: string;
  date_deces?: string;
  periode?: string;
}

class HanafiyaDataApiService {
  // Récupérer les exposants (exposés récents)
  async getExposes(limit: number = 5): Promise<Expose[]> {
    try {
      // Utiliser ordering au lieu de limit pour Django REST Framework
      // La pagination par défaut retourne les résultats dans 'results'
      // Le backend DRF router retourne une structure de routing avec des URLs
      // L'endpoint correct est directement /exposes/exposes/ (pas /exposes/)
      // Essayons d'abord /exposes/ pour détecter la structure, puis utilisons /exposes/exposes/
      let response: any;
      try {
        response = await api.get('/exposes/', {
          params: { 
            page_size: limit > 100 ? 100 : limit,
            ordering: '-date_expose,-heure_debut',
          }
        });
        
        // Si la réponse contient des URLs (structure de routing), extraire le pathname correct
        if (response.data && typeof response.data.exposes === 'string' && response.data.exposes.startsWith('http')) {
          console.log('[HanafiyaData] Reçu des URLs, appel de l\'endpoint direct');
          // Extraire le chemin de l'URL complète et enlever /api/ car api.get() l'ajoute déjà
          const urlPath = new URL(response.data.exposes).pathname;
          const cleanPath = urlPath.replace(/^\/api/, ''); // Enlever /api/ du début
          // Appeler directement l'endpoint
          response = await api.get(cleanPath, {
            params: { 
              page_size: limit > 100 ? 100 : limit,
              ordering: '-date_expose,-heure_debut',
            }
          });
        }
      } catch (firstError: any) {
        // Si /exposes/ échoue ou retourne des URLs, essayer directement /exposes/exposes/
        console.log('[HanafiyaData] Utilisation de l\'endpoint direct /exposes/exposes/');
        response = await api.get('/exposes/exposes/', {
          params: { 
            page_size: limit > 100 ? 100 : limit,
            ordering: '-date_expose,-heure_debut',
          }
        });
      }
      // Le backend peut retourner différentes structures :
      // 1. Structure paginée DRF: { results: [...], count: X, next: null, previous: null }
      // 2. Structure custom: { exposes: [...], commentaires: [...] }
      // 3. Tableau direct: [...]
      console.log('[HanafiyaData] Réponse complète du backend:', {
        hasExposes: !!response.data.exposes,
        exposesType: typeof response.data.exposes,
        exposesIsArray: Array.isArray(response.data.exposes),
        exposesLength: response.data.exposes?.length,
        hasResults: !!response.data.results,
        resultsIsArray: Array.isArray(response.data.results),
        resultsLength: response.data.results?.length,
        isDataArray: Array.isArray(response.data),
        dataKeys: Object.keys(response.data || {}),
        fullResponse: response.data,
      });
      
      let data: any[] = [];
      if (response.data.exposes && Array.isArray(response.data.exposes)) {
        // Structure custom avec exposes et commentaires
        data = response.data.exposes;
        console.log(`[HanafiyaData] Structure custom détectée: ${data.length} exposés dans exposes`);
      } else if (response.data.results && Array.isArray(response.data.results)) {
        // Structure paginée standard DRF
        data = response.data.results;
        console.log(`[HanafiyaData] Structure paginée DRF détectée: ${data.length} exposés dans results`);
      } else if (Array.isArray(response.data)) {
        // Tableau direct
        data = response.data;
        console.log(`[HanafiyaData] Tableau direct détecté: ${data.length} exposés`);
      } else {
        console.warn('[HanafiyaData] Structure de réponse inconnue:', response.data);
      }
      
      const exposes = Array.isArray(data) ? data : [];
      const total = response.data.count || exposes.length;
      console.log(`[HanafiyaData] ${exposes.length} exposés récupérés sur ${total} total(s)`);
      if (exposes.length > 0) {
        console.log(`[HanafiyaData] Premier exposé:`, exposes[0]);
        console.log(`[HanafiyaData] Premier exposé: ${exposes[0].theme} - ${exposes[0].prenom_exposant} ${exposes[0].nom_exposant} (${exposes[0].date_expose})`);
      } else {
        console.warn(`[HanafiyaData] ⚠️ Aucun exposé trouvé dans la réponse.`);
        console.warn(`[HanafiyaData] Structure complète:`, JSON.stringify(response.data, null, 2));
      }
      return exposes;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors du chargement des exposants:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      console.error('[HanafiyaData] Détails:', error.response?.data || error.message);
      return [];
    }
  }

  // Rechercher un exposant par nom ou thème
  async searchExpose(query: string): Promise<Expose | null> {
    try {
      const response = await api.get('/exposes/', {
        params: { search: query, limit: 1 }
      });
      const exposes = response.data.results || response.data || [];
      return exposes.length > 0 ? exposes[0] : null;
    } catch (error) {
      console.error('[HanafiyaData] Erreur lors de la recherche d\'exposant:', error);
      return null;
    }
  }

  // Récupérer les événements (événements à venir)
  async getEvents(limit: number = 5): Promise<Event[]> {
    try {
      // Gérer le routing DRF comme pour les exposés
      let response: any;
      try {
        response = await api.get('/events/', {
          params: { 
            page_size: limit > 100 ? 100 : limit,
            ordering: '-date_event,-heure_debut'
          }
        });
        
        // Si la réponse contient des URLs (structure de routing)
        if (response.data && typeof response.data.events === 'string' && response.data.events.startsWith('http')) {
          const urlPath = new URL(response.data.events).pathname;
          const cleanPath = urlPath.replace(/^\/api/, '');
          response = await api.get(cleanPath, {
            params: { 
              page_size: limit > 100 ? 100 : limit,
              ordering: '-date_event,-heure_debut'
            }
          });
        }
      } catch (firstError: any) {
        // Fallback vers l'endpoint direct si disponible
        response = await api.get('/events/events/', {
          params: { 
            page_size: limit > 100 ? 100 : limit,
            ordering: '-date_event,-heure_debut'
          }
        });
      }
      
      const data = response.data.results || response.data || [];
      const events = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] ${events.length} événements récupérés sur ${response.data.count || events.length} total(s)`);
      if (events.length > 0) {
        console.log(`[HanafiyaData] Premier événement: ${events[0].titre} (${events[0].date_event})`);
      }
      return events;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors du chargement des événements:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      console.error('[HanafiyaData] Détails:', error.response?.data || error.message);
      return [];
    }
  }

  // Rechercher un événement par titre ou description
  async searchEvent(query: string): Promise<Event | null> {
    try {
      const response = await api.get('/events/', {
        params: { 
          search: query, 
          page_size: 1,
          ordering: '-date_event,-heure_debut' // Plus récent d'abord
        }
      });
      const data = response.data.results || response.data || [];
      const events = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] Recherche événement "${query}": ${events.length} résultat(s)`);
      return events.length > 0 ? events[0] : null;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors de la recherche d\'événement:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      return null;
    }
  }

  // Récupérer les daaras
  async getDaaras(limit: number = 10): Promise<Daara[]> {
    try {
      const response = await api.get('/daara/', { // URL correcte : /daara/ (sans s)
        params: { 
          page_size: limit > 100 ? 100 : limit,
          ordering: 'jour,-created_at' // Par jour puis plus récent
        }
      });
      const data = response.data.results || response.data || [];
      const daaras = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] ${daaras.length} daaras récupérés sur ${response.data.count || daaras.length} total(s)`);
      if (daaras.length > 0) {
        console.log(`[HanafiyaData] Premier daara: ${daaras[0].titre} (Jour: ${daaras[0].jour})`);
      }
      return daaras;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors du chargement des daaras:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      console.error('[HanafiyaData] Détails:', error.response?.data || error.message);
      return [];
    }
  }

  // Rechercher un daara par titre ou description
  async searchDaara(query: string): Promise<Daara | null> {
    try {
      const response = await api.get('/daara/', { // URL correcte : /daara/ (sans s)
        params: { 
          search: query, 
          page_size: 1,
          ordering: 'jour,-created_at' // Par jour puis plus récent
        }
      });
      const data = response.data.results || response.data || [];
      const daaras = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] Recherche daara "${query}": ${daaras.length} résultat(s)`);
      return daaras.length > 0 ? daaras[0] : null;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors de la recherche de daara:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      return null;
    }
  }

  // Récupérer les marabouts
  async getMarabouts(limit: number = 10): Promise<Marabout[]> {
    try {
      const response = await api.get('/bibliotheque/marabouts/', {
        params: { 
          page_size: limit > 100 ? 100 : limit,
          ordering: 'nom_complet' // Par ordre alphabétique
        }
      });
      const data = response.data.results || response.data || [];
      const marabouts = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] ${marabouts.length} marabouts récupérés sur ${response.data.count || marabouts.length} total(s)`);
      if (marabouts.length > 0) {
        console.log(`[HanafiyaData] Premier marabout: ${marabouts[0].nom_complet}`);
      }
      return marabouts;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors du chargement des marabouts:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      console.error('[HanafiyaData] Détails:', error.response?.data || error.message);
      return [];
    }
  }

  // Rechercher un marabout par nom
  async searchMarabout(query: string): Promise<Marabout | null> {
    try {
      const response = await api.get('/bibliotheque/marabouts/', {
        params: { 
          search: query, 
          page_size: 1,
          ordering: 'nom_complet' // Par ordre alphabétique
        }
      });
      const data = response.data.results || response.data || [];
      const marabouts = Array.isArray(data) ? data : [];
      console.log(`[HanafiyaData] Recherche marabout "${query}": ${marabouts.length} résultat(s)`);
      return marabouts.length > 0 ? marabouts[0] : null;
    } catch (error: any) {
      console.error('[HanafiyaData] Erreur lors de la recherche de marabout:', error);
      console.error('[HanafiyaData] Status:', error.response?.status);
      return null;
    }
  }

  // Détecter si la question concerne des données Hanafiya (exposants, événements, daaras, marabouts)
  detectHanafiyaQuery(message: string): { type: 'expose' | 'event' | 'daara' | 'marabout' | null; query: string } {
    const lowerMessage = message.toLowerCase();
    
    // Détecter les mots-clés pour exposants (plus permissif)
    if (/exposant|exposé|expose|thème|theme|exposition|exposés|exposes/i.test(lowerMessage)) {
      // Extraire le nom ou le thème, ou retourner le message complet si c'est une question générale
      const match = lowerMessage.match(/(?:exposant|exposé|expose|thème|theme|exposition)\s+(?:de\s+)?(.+?)(?:\?|$)/i);
      if (match && match[1].trim() && match[1].trim().length > 2) {
        return { type: 'expose', query: match[1].trim() };
      }
      // Si c'est juste "exposés récents" ou "parle-moi des exposés", retourner le message
      return { type: 'expose', query: message };
    }
    
    // Détecter les mots-clés pour événements
    if (/événement|evenement|event|balayage|réunion|reunion|dahira|événements|evenements|events/i.test(lowerMessage)) {
      const match = lowerMessage.match(/(?:événement|evenement|event|balayage|réunion|reunion|dahira)\s+(?:de\s+)?(.+?)(?:\?|$)/i);
      if (match && match[1].trim() && match[1].trim().length > 2) {
        return { type: 'event', query: match[1].trim() };
      }
      return { type: 'event', query: message };
    }
    
    // Détecter les mots-clés pour daaras
    if (/daara|dara|cours|enseignement|apprentissage|daaras|daras/i.test(lowerMessage)) {
      const match = lowerMessage.match(/(?:daara|dara|cours|enseignement|apprentissage)\s+(?:de\s+)?(.+?)(?:\?|$)/i);
      if (match && match[1].trim() && match[1].trim().length > 2) {
        return { type: 'daara', query: match[1].trim() };
      }
      return { type: 'daara', query: message };
    }
    
    // Détecter les mots-clés pour marabouts
    if (/marabout|cheikh|imam|savant|scholar|oulama|marabouts/i.test(lowerMessage)) {
      const match = lowerMessage.match(/(?:marabout|cheikh|imam|savant|scholar|oulama)\s+(?:de\s+)?(.+?)(?:\?|$)/i);
      if (match && match[1].trim() && match[1].trim().length > 2) {
        return { type: 'marabout', query: match[1].trim() };
      }
      return { type: 'marabout', query: message };
    }
    
    return { type: null, query: message };
  }
}

export const hanafiyaDataApiService = new HanafiyaDataApiService();

